import setReferrerMeta from 'owa-shared-start/lib/setReferrerMeta';
import WebSessionType from 'owa-service/lib/contract/WebSessionType';
import { addBottleneck } from 'owa-performance';
import { appendMiscData } from 'owa-shared-start/lib/miscData';
import { fetchData } from 'owa-shared-start/lib/fetchData';
import { getItem, removeItem, setItem } from 'owa-local-storage';
import { getItemFromSessionStorage } from 'mini-session-storage';
import { getPathMetadata } from 'mini-deeplink-bootstrap/lib/utils/getPathMetadata';
import { markFunction } from 'owa-performance';
import { ServerAppName } from 'data/lib/mini-app-constants';
import { SessionData } from 'owa-service/lib/types/SessionData';
import { updateDiagnosticsOnSessionData } from 'owa-shared-start/lib/sessionDiagnostics';

const FindFolderParametersKey = 'sdfp';
const FindMessageParametersKey = 'sdmp';

enum LocalStorageFunnel {
    NoLocalStorage = 0,
    LocalStorageFound = 1,
    ApiFoundParams = 2,
    ApiUsedParams = 3,
}

enum StartupDiagnostics {
    None = 0,
    FoundFolderParams = 1,
    FoundMessageParams = 2,
    UsedFolderParams = 4,
    UsedMessageParams = 8,
    JsonException = 16,
}

let findFolderTunnel: LocalStorageFunnel = LocalStorageFunnel.NoLocalStorage;
let findMessageTunnel: LocalStorageFunnel = LocalStorageFunnel.NoLocalStorage;

/**
 * It returns mini startup boot promise which overrides session data logic in shared packages
 * This override is currently not suitable for OPX or deep link flows as some conditions
 * are not handled.
 * Those changes need to be done iteratively as we integrate and test the flow for other bootstrap flows
 * @param authToken
 * @param offlineBootStrategy
 */
export function getStartupBootPromise(app: ServerAppName) {
    const { isTodo } = getPathMetadata();
    const sessionData = getItemFromSessionStorage('session');
    return function (authToken?: any, offlineBootStrategy?: any) {
        const startupDatafn = markFunction(getStartupData, 'sd');
        if (sessionData && isTodo) {
            return Promise.resolve(sessionData);
        }
        return startupDatafn(authToken, offlineBootStrategy, app);
    };
}

function getStartupData(
    authToken?: string,
    offlineBootStrategy?: any,
    app?: ServerAppName
): Promise<SessionData> {
    const headers = new Headers();

    // This header should not be added for Deeplink or findtime as it causes the app to always redirect to mail
    headers.append('X-Js-ClientType', '2');
    headers.append('X-Ignore-Preload', '1');

    appendMiscData('&startup=true');

    if (app === ServerAppName.Mini) {
        // Add the folderParams info as part of header to make it easy for the backend service
        const folderParams = getItem(window, FindFolderParametersKey);
        if (folderParams) {
            headers.append('folderParams', folderParams);
            findFolderTunnel = LocalStorageFunnel.LocalStorageFound;
        }
        // Add the messageParams info as part of header to make it easy for the backend service
        const messageParams = getItem(window, FindMessageParametersKey);
        if (messageParams) {
            headers.append('messageParams', messageParams);
            findMessageTunnel = LocalStorageFunnel.LocalStorageFound;
        }
    } else if (app === ServerAppName.MiniPeople || app === ServerAppName.MiniCalendar) {
        headers.append('X-Folder-Count', '0');
        headers.append('X-Message-Count', '0');
    }

    // This is done in order to combine user boot settings data in the startup data
    headers.append('X-Js-Experiment', '1');

    return fetchData<SessionData>(
        'startupdata.ashx',
        'StartupData',
        null,
        headers,
        postProcess,
        processHeaders
    );
}

function postProcess(settings: SessionData) {
    if (settings.owaUserConfig.SessionSettings.WebSessionType !== WebSessionType.Business) {
        // This is a tag set for SEO purposes and for any analytics.
        // Keeping it as it is present in shared package.
        setReferrerMeta('origin');
    }

    // Persist the folder and message params in local storage.
    settings.folderParams &&
        setItem(window, FindFolderParametersKey, JSON.stringify(settings.folderParams));
    settings.messageParams &&
        setItem(window, FindMessageParametersKey, JSON.stringify(settings.messageParams));

    // Called to update boot diagnostics
    updateDiagnosticsOnSessionData(settings);

    return settings;
}

function processHeaders(headers: Headers) {
    /**
     * The below logic is needed for marking bottlenecks which are included
     * in the aria boot reliability event.
     **/
    const diagnostics = headers?.get('x-owa-startup-diag');
    const startupDiagnostics: StartupDiagnostics | undefined = diagnostics && parseInt(diagnostics);
    addLocalStorageBottleneck(
        FindFolderParametersKey,
        findFolderTunnel,
        startupDiagnostics,
        StartupDiagnostics.UsedFolderParams,
        StartupDiagnostics.FoundFolderParams
    );
    addLocalStorageBottleneck(
        FindMessageParametersKey,
        findMessageTunnel,
        startupDiagnostics,
        StartupDiagnostics.UsedMessageParams,
        StartupDiagnostics.FoundMessageParams
    );
}

function addLocalStorageBottleneck(
    bottleneck: string,
    funnel: LocalStorageFunnel,
    diagnostics: StartupDiagnostics | undefined,
    useParams: StartupDiagnostics,
    foundParams: StartupDiagnostics
) {
    let funnelVal = funnel;
    if (diagnostics) {
        if ((diagnostics & useParams) !== 0) {
            funnelVal = LocalStorageFunnel.ApiUsedParams;
        } else if ((diagnostics & foundParams) !== 0) {
            funnelVal = LocalStorageFunnel.ApiFoundParams;
        }

        // if there was a json exception in startupdata then local storage is not valid so
        // let's delete it so we don't use it next time
        if ((diagnostics & StartupDiagnostics.JsonException) !== 0) {
            removeItem(window, bottleneck);
        }
    }
    addBottleneck(bottleneck, funnelVal);
}
